"use client"

import { useState } from "react"
import { useDispatch } from "react-redux"
import { registerFaculty, fetchFaculty } from "../store/slices/facultySlice"
import { X } from "lucide-react"

// Define InputField outside AddFaculty
const InputField = ({ label, name, type, value, placeholder, autoComplete, inputMode, required = true, onChange }) => {
  console.log(`Rendering InputField: ${name}, value: ${value}`) // Debug render
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-1">
        {label}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        required={required}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out shadow-sm"
        placeholder={placeholder}
        autoComplete={autoComplete}
        {...(inputMode && { inputMode })}
        {...(type === "text" && (name.includes("id") || name.includes("years")) ? { pattern: "[0-9]*" } : {})}
      />
    </div>
  )
}

const AddFaculty = ({ setShowAddForm }) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    employee_id: "",
    department_code: "", // was department_id
    designation: "",
    phone: "",
    qualification: "",
    experience_years: "",
    joining_date: "",
  })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    console.log(`Input changed: ${name} = ${value}`) // Debugging
    if (name === "experience_years") {
      if (value && !/^\d*$/.test(value)) {
        return
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const submissionData = {
        ...formData,
        experience_years: formData.experience_years ? Number.parseInt(formData.experience_years, 10) : null,
      }

      const result = await dispatch(registerFaculty(submissionData)).unwrap()
      if (result) {
        dispatch(fetchFaculty())
        setShowAddForm(false)
        setFormData({
          name: "",
          email: "",
          password: "",
          employee_id: "",
          department_code: "",
          designation: "",
          phone: "",
          qualification: "",
          experience_years: "",
          joining_date: "",
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to register faculty")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-4xl w-full mx-auto border-t-4 border-blue-600">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h3 className="text-2xl font-bold text-blue-900">Add New Faculty Member</h3>
        <button
          onClick={() => setShowAddForm(false)}
          className="text-gray-500 hover:text-blue-600 p-2 rounded-full transition-colors"
          aria-label="Close form"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-300 text-red-700 rounded-lg font-medium">
          <span className="font-bold">Error:</span> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on">
        <p className="text-lg font-bold text-blue-800 border-b pb-1">Personal & Account Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="Full Name"
            name="name"
            type="text"
            value={formData.name}
            placeholder="e.g., Jane Doe"
            autoComplete="name"
            onChange={handleChange}
          />
          <InputField
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            placeholder="e.g., jane.doe@school.edu"
            autoComplete="email"
            onChange={handleChange}
          />
          <InputField
            label="Initial Password"
            name="password"
            type="password"
            value={formData.password}
            placeholder="Set a temporary password"
            autoComplete="new-password"
            onChange={handleChange}
          />
          <InputField
            label="Phone Number"
            name="phone"
            type="text"
            value={formData.phone}
            placeholder="Enter 10-digit number"
            autoComplete="tel"
            inputMode="tel"
            onChange={handleChange}
          />
        </div>

        <p className="text-lg font-bold text-blue-800 border-b pb-1 pt-4">Employment & Academic Info</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="Employee ID"
            name="employee_id"
            type="text"
            value={formData.employee_id}
            placeholder="e.g., 101"
            autoComplete="off"
            onChange={handleChange}
          />
          <InputField
            label="Institute Code"
            name="department_code"
            type="text"
            value={formData.department_code}
            placeholder="e.g., CSE"
            onChange={handleChange}
          />
          <InputField
            label="Designation"
            name="designation"
            type="text"
            value={formData.designation}
            placeholder="e.g., Assistant Professor"
            autoComplete="off"
            onChange={handleChange}
          />
          <InputField
            label="Qualification"
            name="qualification"
            type="text"
            value={formData.qualification}
            placeholder="e.g., M.Tech, Ph.D."
            autoComplete="off"
            onChange={handleChange}
          />
          <InputField
            label="Experience (Years)"
            name="experience_years"
            type="text"
            value={formData.experience_years}
            placeholder="e.g., 5"
            inputMode="numeric"
            onChange={handleChange}
          />
          <InputField
            label="Joining Date"
            name="joining_date"
            type="date"
            value={formData.joining_date}
            autoComplete="off"
            onChange={handleChange}
          />
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => setShowAddForm(false)}
            className="px-6 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 font-medium rounded-lg transition-colors shadow-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 bg-blue-600 hover:bg-blue-800 text-white font-semibold rounded-lg transition-colors shadow-lg ${
              loading ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <div className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Registering...
              </div>
            ) : (
              "Register Faculty"
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddFaculty
